import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState, useEffect, useMemo } from 'react';
import { POSLayout } from '@/features/pos/components/POSLayout';
import { POSHeader } from '@/features/pos/components/POSHeader';
import { POSTabs } from '@/features/pos/components/POSTabs';
import { CategoryTabs, Category } from '@/features/pos/components/CategoryTabs';
import { ProductGrid } from '@/features/pos/components/ProductGrid';
import { Product } from '@/features/pos/components/ProductCard';
import { Cart } from '@/features/pos/components/Cart';
import { CheckoutModal } from '@/features/pos/components/CheckoutModal';
import { ShortcutsHelpModal } from '@/features/pos/components/ShortcutsHelpModal';
import { TableFloorView } from '@/features/pos/components/TableFloorView';
import { usePOSCartStore } from '@/features/pos/stores/posCartStore';
import { useProducts } from '@/hooks/queries/useProducts';
import { useAuthStore } from '@/stores/authStore';
import { useBusinessType } from '@/features/pos/hooks/useBusinessType';
import { usePOSShopSettings } from '@/features/pos/hooks/useShopSettings';
import { useReplaceOrderItems } from '@/features/pos/hooks/usePOSOrders';
import { useCreateCounterOrder } from '@/features/pos/hooks/useCounterOrders';
import { useKeyboardShortcuts, POS_SHORTCUTS } from '@/features/pos/hooks/useKeyboardShortcuts';
import { Coffee, UtensilsCrossed, Cake, Droplet, Package, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { toast } from '@/components/ui/toast/useToast';
import type { POSOrderWithItems } from '@/features/pos/types/admin';

export const Route = createFileRoute('/apps/pos/')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: POSPage,
});

type POSTab = 'products' | 'tables';

function POSPage() {
  const { company } = useAuthStore();
  const { data: productsData = [], isLoading } = useProducts(company?.id || '');
  const { data: shopSettings } = usePOSShopSettings();
  const { features } = useBusinessType();
  const {
    items,
    addItem,
    linkedTableNumber,
    linkedOrderId,
    customerName,
    orderNotes,
    clearCart,
    setVatEnabled,
    getSubtotal,
    getVATAmount,
    getFiscalStamp,
    getTotal,
  } = usePOSCartStore();
  const replaceOrderItems = useReplaceOrderItems();
  const createCounterOrder = useCreateCounterOrder();

  const [activeTab, setActiveTab] = useState<POSTab>('products');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCheckout, setShowCheckout] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);

  // Sync VAT enabled setting from shop settings
  useEffect(() => {
    if (shopSettings) {
      setVatEnabled(shopSettings.enable_vat !== false);
    }
  }, [shopSettings, setVatEnabled]);

  // Categories
  const categories: Category[] = [
    { id: 'beverages', name: 'Boissons', nameAr: 'مشروبات', color: '#3B82F6', icon: Coffee },
    { id: 'food', name: 'Nourriture', nameAr: 'طعام', color: '#F97316', icon: UtensilsCrossed },
    { id: 'desserts', name: 'Desserts', nameAr: 'حلويات', color: '#EC4899', icon: Cake },
    { id: 'drinks', name: 'Jus', nameAr: 'عصائر', color: '#10B981', icon: Droplet },
    { id: 'other', name: 'Autre', nameAr: 'أخرى', color: '#6B7280', icon: Package },
  ];

  // Filter products
  let filteredProducts = productsData;

  if (selectedCategory) {
    filteredProducts = filteredProducts.filter((p) => p.category === selectedCategory);
  }

  if (searchTerm) {
    const term = searchTerm.toLowerCase();
    filteredProducts = filteredProducts.filter((p) =>
      p.name?.toLowerCase().includes(term) ||
      p.code?.toLowerCase().includes(term) ||
      p.barcode?.toLowerCase().includes(term)
    );
  }

  const handleAddToCart = (product: Product) => {
    if (product.stock_quantity <= 0) {
      toast.error('خطأ / Erreur', 'المخزون غير كافٍ\nStock insuffisant');
      return;
    }

    addItem({
      productId: product.id,
      productCode: product.code,
      productName: product.name,
      unitPrice: product.sale_price,
      vat: product.vat || 19,
      discount: 0,
      discountType: 'percentage',
    });

    toast.success('تمت الإضافة / Ajouté', product.name);
  };

  // Save table order
  const handleSaveOrder = async () => {
    if (!linkedOrderId) {
      toast.error('Erreur', 'Aucune table sélectionnée');
      return;
    }

    if (items.length === 0) {
      toast.error('Erreur', 'Panier vide');
      return;
    }

    try {
      await replaceOrderItems.mutateAsync({
        order_id: linkedOrderId,
        items: items.map(item => ({
          product_id: item.productId,
          product_code: item.productCode,
          product_name: item.productName,
          quantity: item.quantity,
          unit_price: item.unitPrice,
          vat_rate: item.vat,
          discount_rate: item.discount,
          notes: item.notes,
        })),
      });

      toast.success('Sauvegardé', `Commande sauvegardée pour ${linkedTableNumber}`);

      clearCart();
      setActiveTab('tables');
    } catch (error: any) {
      toast.error('Erreur', error.message);
    }
  };

  // Open checkout modal for counter sales
  const handleCheckout = () => {
    if (items.length === 0) {
      toast.error('Erreur', 'Panier vide / السلة فارغة');
      return;
    }

    if (linkedOrderId) {
      // Table order - save directly
      handleSaveOrder();
    } else {
      // Counter order - open payment modal
      setShowCheckout(true);
    }
  };

  // Process payment from checkout modal
  const handleCompletePayment = async (paymentData: {
    payment_method: string;
    payment_amount: number;
    change_amount: number;
  }): Promise<POSOrderWithItems> => {
    const order = await createCounterOrder.mutateAsync({
      order_type: 'counter',
      customer_name: customerName || undefined,
      notes: orderNotes || undefined,
      payment_method: paymentData.payment_method,
      payment_amount: paymentData.payment_amount,
      change_amount: paymentData.change_amount,
    });

    return order as POSOrderWithItems;
  };

  // Handle new sale after checkout
  const handleNewSale = () => {
    clearCart();
    setShowCheckout(false);
  };

  const handleSwitchToProducts = () => {
    setActiveTab('products');
  };

  // Keyboard shortcuts
  const shortcuts = useMemo(() => [
    {
      ...POS_SHORTCUTS.CHECKOUT,
      action: () => handleCheckout(),
    },
    {
      ...POS_SHORTCUTS.CASH_PAYMENT,
      action: () => {
        if (items.length === 0) {
          toast.error('Erreur', 'Panier vide / السلة فارغة');
          return;
        }
        setShowCheckout(true);
      },
    },
    {
      ...POS_SHORTCUTS.CLEAR_CART,
      action: () => {
        if (items.length > 0) {
          clearCart();
          toast.success('Panier vidé', 'السلة فارغة');
        }
      },
    },
    {
      ...POS_SHORTCUTS.SHOW_SHORTCUTS,
      action: () => setShowShortcuts(true),
    },
  ], [items.length, clearCart]);

  useKeyboardShortcuts(shortcuts, !showCheckout);

  if (isLoading) {
    return (
      <POSLayout
        header={<POSHeader onShowShortcuts={() => setShowShortcuts(true)} />}
        cart={<Cart onCheckout={handleCheckout} linkedTable={linkedTableNumber} />}
      >
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
        </div>
      </POSLayout>
    );
  }

  return (
    <>
      <POSLayout
        header={<POSHeader onShowShortcuts={() => setShowShortcuts(true)} />}
        cart={activeTab === 'products' ? (
          <Cart
            onCheckout={handleCheckout}
            linkedTable={linkedTableNumber}
            onSave={linkedOrderId ? handleSaveOrder : undefined}
            isSaving={replaceOrderItems.isPending || createCounterOrder.isPending}
          />
        ) : undefined}
      >
        {/* Tabs */}
        <POSTabs activeTab={activeTab} onTabChange={setActiveTab} />

        {activeTab === 'products' ? (
          <>
            {/* Search Bar */}
            <div className="px-4 pt-4 pb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="بحث / Rechercher (Nom, Code, Code-barres)"
                  className="pl-10 h-12 text-base"
                />
              </div>
            </div>

            {/* Category Tabs */}
            <CategoryTabs
              categories={categories}
              selectedCategory={selectedCategory}
              onSelectCategory={setSelectedCategory}
            />

            {/* Product Grid */}
            <div className="p-4">
              <ProductGrid
                products={filteredProducts}
                onAddToCart={handleAddToCart}
                layout="grid"
                gridSize="3x4"
              />
            </div>
          </>
        ) : (
          /* Tables Floor View */
          features.hasTableManagement && (
            <TableFloorView onSwitchToProducts={handleSwitchToProducts} />
          )
        )}
      </POSLayout>

      {/* Checkout Modal */}
      <CheckoutModal
        open={showCheckout}
        onClose={() => setShowCheckout(false)}
        total={getTotal()}
        subtotal={getSubtotal()}
        vatAmount={getVATAmount()}
        fiscalStamp={getFiscalStamp()}
        onComplete={handleCompletePayment}
        onNewSale={handleNewSale}
      />

      {/* Shortcuts Help Modal */}
      <ShortcutsHelpModal
        open={showShortcuts}
        onClose={() => setShowShortcuts(false)}
      />
    </>
  );
}
